import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import apiClient from '../services/api';
import './CommentSettings.css';

const PLATFORMS = [
  { key: 'instagram', icon: '📸', endpoint: '/api/v1/comments/settings' },
  { key: 'facebook', icon: '📘', endpoint: '/api/v1/fb-comments/settings' },
  { key: 'tiktok', icon: '🎵', endpoint: '/api/v1/tiktok-comments/settings' },
];

function CommentSettings() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith('en') ? 'en' : 'it';
  const [archetypes, setArchetypes] = useState([]);
  const [userArchetype, setUserArchetype] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [noAccount, setNoAccount] = useState(false);
  const [platform, setPlatform] = useState('instagram');

  const [autoReply, setAutoReply] = useState(true);
  const [autoLikePositive, setAutoLikePositive] = useState(true);
  const [tone, setTone] = useState(null);
  const [referralLink, setReferralLink] = useState('');
  const [referralMessage, setReferralMessage] = useState('');

  // Track which platforms are available
  const [hasInstagram, setHasInstagram] = useState(false);
  const [hasFacebook, setHasFacebook] = useState(false);
  const [hasTikTok, setHasTikTok] = useState(false);

  // TikTok Business API status
  const [tiktokBusinessConnected, setTiktokBusinessConnected] = useState(false);
  const [tiktokBusinessId, setTiktokBusinessId] = useState(null);
  const [connectingBusiness, setConnectingBusiness] = useState(false);

  useEffect(() => {
    checkAvailablePlatforms();
    fetchArchetypes();
    fetchUserArchetype();
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [platform]);

  const checkAvailablePlatforms = async () => {
    try {
      const [igRes, fbRes, ttRes] = await Promise.allSettled([
        apiClient.get('/api/v1/comments/settings'),
        apiClient.get('/api/v1/fb-comments/settings'),
        apiClient.get('/api/v1/tiktok-comments/settings'),
      ]);
      const igOk = igRes.status === 'fulfilled' && igRes.value.status === 200;
      const fbOk = fbRes.status === 'fulfilled' && fbRes.value.status === 200;
      const ttOk = ttRes.status === 'fulfilled' && ttRes.value.status === 200;
      setHasInstagram(igOk);
      setHasFacebook(fbOk);
      setHasTikTok(ttOk);

      // Set active platform to first available
      if (igOk) {
        setPlatform('instagram');
      } else if (fbOk) {
        setPlatform('facebook');
      } else if (ttOk) {
        setPlatform('tiktok');
      }
    } catch {
      // Ignore — individual fetch will handle errors
    }
  };

  const currentEndpoint = PLATFORMS.find(p => p.key === platform)?.endpoint || PLATFORMS[0].endpoint;

  const fetchArchetypes = async () => {
    try {
      const res = await apiClient.get('/api/v1/tone-archetypes');
      setArchetypes(res.data);
    } catch {
      // Non-blocking — tone picker will just be empty
    }
  };

  const fetchUserArchetype = async () => {
    try {
      const res = await apiClient.get('/api/v1/users/me/tone-archetype');
      setUserArchetype(res.data?.tone_archetype || null);
    } catch {
      // Non-blocking — fallback indicator will be omitted
    }
  };

  const fetchSettings = async () => {
    setLoading(true);
    setError('');
    setNoAccount(false);
    try {
      const response = await apiClient.get(currentEndpoint);
      const data = response.data;
      setAutoReply(data.auto_reply_comments);
      setAutoLikePositive(data.auto_like_positive);
      setTone(data.comment_tone ?? null);
      setReferralLink(data.referral_link || '');
      setReferralMessage(data.referral_message || '');
      setNoAccount(false);

      // Track TikTok Business API status
      if (platform === 'tiktok') {
        setTiktokBusinessConnected(!!data.has_business_token);
        setTiktokBusinessId(data.business_id || null);
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setNoAccount(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    setError('');
    try {
      await apiClient.put(currentEndpoint, {
        auto_like_positive: autoLikePositive,
        auto_reply_comments: autoReply,
        comment_tone: tone,
        referral_link: referralLink || null,
        referral_message: referralMessage || null,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      setError(t('comments.settingsError'));
    } finally {
      setSaving(false);
    }
  };

  // ── TikTok Business API OAuth popup ──
  const handleConnectTikTokBusiness = async () => {
    setConnectingBusiness(true);
    try {
      const res = await apiClient.get('/api/v1/social/tiktok/business/oauth/start');
      const { oauth_url } = res.data;
      const popup = window.open(oauth_url, 'tiktok_business_oauth', 'width=600,height=700');

      const onMessage = (event) => {
        if (event.data?.type === 'TIKTOK_BUSINESS_OAUTH_CALLBACK') {
          window.removeEventListener('message', onMessage);
          setConnectingBusiness(false);
          if (event.data.success) {
            setTiktokBusinessConnected(true);
            setTiktokBusinessId(event.data.business_id);
          } else {
            setError(event.data.error || t('comments.businessConnectError'));
          }
          if (popup) popup.close();
        }
      };
      window.addEventListener('message', onMessage);

      // Fallback: close loading state if popup is closed without completing
      const checkClosed = setInterval(() => {
        if (popup && popup.closed) {
          clearInterval(checkClosed);
          window.removeEventListener('message', onMessage);
          setConnectingBusiness(false);
        }
      }, 1000);
    } catch {
      setError(t('comments.businessConnectError'));
      setConnectingBusiness(false);
    }
  };

  if (loading) {
    return (
      <div className="comment-settings">
        <div className="comment-settings-loading">{t('common.loading')}</div>
      </div>
    );
  }

  if (noAccount) {
    const noAccountIcon = platform === 'tiktok' ? '🎵' : platform === 'facebook' ? '📘' : '📸';
    const noAccountMsg = platform === 'tiktok'
      ? t('comments.noTikTokAccount')
      : platform === 'facebook'
        ? t('comments.noFacebookAccount')
        : t('comments.noAccount');
    return (
      <div className="comment-settings">
        <div className="comment-settings-empty">
          <span className="empty-icon">{noAccountIcon}</span>
          <p>{noAccountMsg}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="comment-settings">
      {/* Platform Tabs */}
      {(hasInstagram || hasFacebook || hasTikTok) && (
        <div className="cs-platform-tabs">
          {hasInstagram && (
            <button
              className={`cs-platform-tab ${platform === 'instagram' ? 'active' : ''}`}
              onClick={() => setPlatform('instagram')}
            >
              📸 Instagram
            </button>
          )}
          {hasFacebook && (
            <button
              className={`cs-platform-tab ${platform === 'facebook' ? 'active' : ''}`}
              onClick={() => setPlatform('facebook')}
            >
              📘 Facebook
            </button>
          )}
          {hasTikTok && (
            <button
              className={`cs-platform-tab ${platform === 'tiktok' ? 'active' : ''}`}
              onClick={() => setPlatform('tiktok')}
            >
              🎵 TikTok
            </button>
          )}
        </div>
      )}

      {/* TikTok Business API Connection Status */}
      {platform === 'tiktok' && (
        <div className={`cs-business-status ${tiktokBusinessConnected ? 'connected' : 'disconnected'}`}>
          {tiktokBusinessConnected ? (
            <div className="cs-business-info">
              <span className="cs-business-badge">✅ {t('comments.businessApiConnected')}</span>
              <span className="cs-business-detail">
                {t('comments.businessId')}: {tiktokBusinessId}
              </span>
            </div>
          ) : (
            <div className="cs-business-info">
              <span className="cs-business-badge warning">⚠️ {t('comments.businessApiDisconnected')}</span>
              <p className="cs-business-hint">{t('comments.businessApiHint')}</p>
              <button
                className="cs-business-connect-btn"
                onClick={handleConnectTikTokBusiness}
                disabled={connectingBusiness}
              >
                {connectingBusiness ? t('common.loading') : t('comments.connectBusinessApi')}
              </button>
            </div>
          )}
        </div>
      )}

      <div className="comment-settings-header">
        <h3>💬 {t('comments.settings')}</h3>
        <p className="comment-settings-subtitle">{t('comments.settingsDescription')}</p>
      </div>

      {/* Auto-Reply Toggle */}
      <div className="cs-field">
        <div className="cs-toggle-row">
          <div className="cs-toggle-info">
            <label className="cs-label">🤖 {t('comments.autoReply')}</label>
            <span className="cs-hint">{t('comments.autoReplyDescription')}</span>
          </div>
          <label className="cs-switch">
            <input
              type="checkbox"
              checked={autoReply}
              onChange={(e) => setAutoReply(e.target.checked)}
            />
            <span className="cs-slider"></span>
          </label>
        </div>
      </div>

      {/* Auto-Like Positive Comments Toggle */}
      <div className="cs-field">
        <div className="cs-toggle-row">
          <div className="cs-toggle-info">
            <label className="cs-label">❤️ {t('comments.autoLikePositive')}</label>
            <span className="cs-hint">{t('comments.autoLikePositiveDescription')}</span>
          </div>
          <label className="cs-switch">
            <input
              type="checkbox"
              checked={autoLikePositive}
              onChange={(e) => setAutoLikePositive(e.target.checked)}
            />
            <span className="cs-slider"></span>
          </label>
        </div>
      </div>

      {/* Tone Selector */}
      {autoReply && (
        <div className="cs-field">
          <label className="cs-label">🎨 {t('comments.tone')}</label>
          <span className="cs-hint">{t('comments.toneDescription')}</span>
          <div className="cs-tone-grid">
            {/* "Use profile archetype" card — saves null. Selected when no override
                is set OR when the saved value isn't a known archetype id (legacy). */}
            {(() => {
              const isOverrideValid = !!tone && archetypes.some((a) => a.id === tone);
              const isDefaultSelected = !isOverrideValid;
              const profileArchetype = archetypes.find((a) => a.id === userArchetype);
              const profileLabel = profileArchetype
                ? (lang === 'en' ? profileArchetype.label_en : profileArchetype.label_it)
                : null;
              return (
                <button
                  className={`cs-tone-card ${isDefaultSelected ? 'selected' : ''}`}
                  onClick={() => setTone(null)}
                  type="button"
                >
                  <span className="cs-tone-icon">🌐</span>
                  <span className="cs-tone-label">
                    {lang === 'en' ? 'Profile default' : 'Default profilo'}
                  </span>
                  <span className="cs-tone-tagline">
                    {profileLabel
                      ? (lang === 'en'
                          ? `Uses: ${profileLabel}`
                          : `Usa: ${profileLabel}`)
                      : (lang === 'en'
                          ? 'Uses your profile voice archetype'
                          : 'Usa il tuo archetipo di voce')}
                  </span>
                </button>
              );
            })()}
            {archetypes.map((a) => (
              <button
                key={a.id}
                className={`cs-tone-card ${tone === a.id ? 'selected' : ''}`}
                onClick={() => setTone(a.id)}
                type="button"
              >
                <span className="cs-tone-icon">{a.icon}</span>
                <span className="cs-tone-label">
                  {lang === 'en' ? a.label_en : a.label_it}
                </span>
                <span className="cs-tone-tagline">
                  {lang === 'en' ? a.tagline_en : a.tagline_it}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Referral Link */}
      <div className="cs-field">
        <label className="cs-label">🔗 {t('comments.referralLink')}</label>
        <span className="cs-hint">{t('comments.referralLinkDescription')}</span>
        <input
          type="url"
          className="cs-input"
          placeholder={t('comments.referralLinkPlaceholder')}
          value={referralLink}
          onChange={(e) => setReferralLink(e.target.value)}
        />
      </div>

      {/* Referral Message */}
      <div className="cs-field">
        <label className="cs-label">✉️ {t('comments.referralMessage')}</label>
        <span className="cs-hint">{t('comments.referralMessageDescription')}</span>
        <textarea
          className="cs-textarea"
          placeholder={t('comments.referralMessagePlaceholder')}
          value={referralMessage}
          onChange={(e) => setReferralMessage(e.target.value)}
          rows={3}
        />
      </div>

      {/* Save Button */}
      <div className="cs-actions">
        <button
          className="cs-save-btn"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? t('common.loading') : t('comments.saveSettings')}
        </button>
        {saved && <span className="cs-success-msg">✅ {t('comments.settingsSaved')}</span>}
        {error && <span className="cs-error-msg">❌ {error}</span>}
      </div>
    </div>
  );
}

export default CommentSettings;
